import { inject, Inject, Injectable } from '@angular/core';
import {
  ApiDataAccess,
  ResourceService,
  ResponseRequestEvent,
  SOFTLINE_SERVICE_HTTP,
} from '@softline/core';
import { filter, firstValueFrom, lastValueFrom, map, Observable } from 'rxjs';
import {
  SOFTLINE_API_KOMMSCAN,
  SOFTLINE_API_KOMMSCAN_MISCHPALETTE,
  SOFTLINE_API_KOPFKOMM_ABSCHLUSS,
  SOFTLINE_API_KOPFKOMM_ABSCHLUSS_MISCHPALETTE,
  SOFTLINE_API_KOPFKOMM_ABSCHLUSS_MISCHPALETTE_DRUCK,
  SOFTLINE_API_KOPFKOMM_BEGLEITSCHEINDRUCK,
  SOFTLINE_API_KOPFKOMM_SPERRE,
  SOFTLINE_API_LAGERPLATZVORSCHLAG,
  SOFTLINE_API_SCAN_LAGERPLATZ,
  SOFTLINE_API_SCAN_PALETTE,
} from '../kommissionierung.api';
import { Kommscan } from '../types/kommscan';
import {
  KommissionsauftragBewe,
  KommissionsauftragKopf,
} from '../types/kommissionsauftrag';
import {
  ArtikelMenge,
  Lagerplatz,
  LagerplatzInhalt,
  MischPalette,
  Palette,
} from '@softapps/lager/core';
import { CancellationParams } from '../dialogs/cancellation-modal/cancellation-modal.component';

@Injectable({ providedIn: 'root' })
export class KommissionierungService {
  private readonly dataAccess = inject(ApiDataAccess);

  constructor(@Inject(SOFTLINE_SERVICE_HTTP) private service: ResourceService<any>) {}

  loadMischpaletten(id: number): Promise<
    {
      positionen: ArtikelMenge[];
      dimensionen: { height: number; width: number; depth: number; massUnitId: string };
      palette: Palette;
    }[]
  > {
    return firstValueFrom(
      this.service.get({
        path: '/v1/kopfkomm/{{id}}/paletten',
        pathParams: { id },
      })
    );
  }

  loadArtikelMenge(id: number): Promise<ArtikelMenge> {
    return firstValueFrom(
      this.service.get({
        path: '/v1/kopfkomm/artikel/{{id}}',
        pathParams: { id },
      })
    );
  }

  createAdditionalItem(id: number, body: ArtikelMenge): Promise<KommissionsauftragBewe> {
    return firstValueFrom(
      this.service.create(
        {
          path: '/v1/kopfkomm/{{id}}/zusatz-position',
          pathParams: { id },
        },
        body
      )
    );
  }

  updateMischpalette(
    id: number,
    idpalette: number,
    body: object
  ): Promise<MischPalette[]> {
    return firstValueFrom(
      this.service.patch(
        {
          path: '/v1/kopfkomm/{{id}}/paletten/{{idpalette}}',
          pathParams: { id, idpalette },
        },
        body
      )
    );
  }

  getLagerplatz(code: string): Observable<LagerplatzInhalt> {
    const location = {
      path: SOFTLINE_API_SCAN_LAGERPLATZ,
      pathParams: { code },
    };
    return this.service.get(location);
  }

  getLagerplatzVorschlag(idbewekomm: number): Observable<LagerplatzInhalt> {
    const location = {
      path: SOFTLINE_API_LAGERPLATZVORSCHLAG,
      pathParams: {
        idbewekomm,
      },
    };
    return this.service.get(location);
  }

  getPaletteInhalt(code: string): Observable<LagerplatzInhalt> {
    return this.service.get({
      path: SOFTLINE_API_SCAN_PALETTE,
      pathParams: { code },
    });
  }

  artikelKommissionierung(kommscan: Kommscan): Observable<KommissionsauftragKopf> {
    const location = { path: SOFTLINE_API_KOMMSCAN };
    return this.service.get(location, kommscan);
  }

  mischpaletteKommissionierung(params: MischPalette): Observable<KommissionsauftragKopf> {
    const location = { path: SOFTLINE_API_KOMMSCAN_MISCHPALETTE };
    return this.service.create(location, params);
  }

  abschluss(id: number): Observable<KommissionsauftragKopf> {
    const location = {
      path: SOFTLINE_API_KOPFKOMM_ABSCHLUSS,
      pathParams: { id },
    };
    return this.service.create(location, undefined);
  }

  completeUnfinished(
    id: number,
    variant: 'NEUER_KOMMAUFTRAG' | 'STORNO_REST' | 'FOLGEBELEG',
    payload?: CancellationParams
  ): Promise<KommissionsauftragKopf | null> {
    const location = {
      path: `${SOFTLINE_API_KOPFKOMM_ABSCHLUSS}/${variant}`,
      pathParams: { id },
    };

    return lastValueFrom(
      this.dataAccess
        .upload(
          location,
          payload
            ? {
                vermerk: payload?.vermerk ?? '',
                files: [payload.signature],
                filename: 'signature.png',
              }
            : {}
        )
        .pipe(
          filter(
            (o): o is ResponseRequestEvent<KommissionsauftragKopf> =>
              !!o && !!o['response']
          ),
          map((o) => o?.response)
        )
    );
  }

  abschlussMischpalette(
    id: number,
    lagerplatz: Lagerplatz | null
  ): Observable<KommissionsauftragKopf> {
    const location = {
      path: SOFTLINE_API_KOPFKOMM_ABSCHLUSS_MISCHPALETTE,
      pathParams: { id },
    };
    return this.service.create(location, lagerplatz);
  }

  begleitscheinDruck(id: number, anzahl: number): Observable<KommissionsauftragKopf> {
    const location = {
      path: SOFTLINE_API_KOPFKOMM_BEGLEITSCHEINDRUCK,
      pathParams: { id },
      queryParams: { anzahl },
    };
    return this.service.create(location, undefined);
  }

  vorkommissionierungDruck(
    id: number,
    anzahl: number
  ): Observable<KommissionsauftragKopf> {
    const location = {
      path: SOFTLINE_API_KOPFKOMM_ABSCHLUSS_MISCHPALETTE_DRUCK,
      pathParams: { id },
      queryParams: { anzahl },
    };
    return this.service.create(location, undefined);
  }

  sperren(id: number, force = false): Observable<KommissionsauftragKopf> {
    const location = {
      path: SOFTLINE_API_KOPFKOMM_SPERRE,
      pathParams: { id },
      queryParams: { force },
    };
    return this.service.create(location, undefined);
  }
}
